import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Chip, Grid, IconButton, Stack, Typography } from "@mui/material";
import { emptySlots, getAisleData, getFranchiseMachineDetails, getMachineDetails, updateBulkSlot, updateMachineSettings } from "../../../Services/authService";
import WestIcon from '@mui/icons-material/West';
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import TemperatureAndDefrostSettings from "./TemperatureAndDefrostSettings";
import SlotCard from "./SlotCard";
import DietaryPreferencesModal from "./DietaryPreferencesModal";
import { useLocation } from 'react-router-dom';
import ConfirmationModal from "../../../Components/Common/ConfirmationModal";
import { VegetarianIcon, VeganIcon, FishIcon, GlutenIcon, LactoseIcon, ProteinIcon } from '../../../Components/Layouts/CustomIcons';
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import ProductModal1 from "./ProductModal1";
import useMediaQuery from '@mui/material/useMediaQuery';
import TempReadings from "./TempReadings";
import useAuth from "../../../Hooks/useAuth";

const tags = [
    { IconComponent: VegetarianIcon, label: "Vegetarian" },
    { IconComponent: VeganIcon, label: "Vegan" },
    { IconComponent: FishIcon, label: "Fish-Free" },
    { IconComponent: GlutenIcon, label: "Gluten-Free" },
    { IconComponent: LactoseIcon, label: "Lactose-Free" },
    { IconComponent: ProteinIcon, label: "High Protein" },
];

const MachineDetails = () => {
    const { id } = useParams(); // Get the machine ID from the URL
    const location = useLocation();
    const { user } = useAuth();
    const { franchise } = location.state || {};

    const [machineData, setMachineData] = useState(null);
    const [aisleData, setAisleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const { t } = useTranslation();
    const { mode } = useThemeContext();
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState("machine");
    const [successNotification, setSuccessNotification] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [error, setError] = useState("");
    const [settings, setSettings] = useState({
        minTemperature: 0,
        maxTemperature: 0,
        cabinetTemperature: 0,
        heatingTemperature: 0,
        preHeatTemperature: 0,
        heatingTime: 0,
        defrostInterval: 0,
        defrostDuration: 0,
    });
    const [selectedSlotIds, setSelectedSlotIds] = useState(new Set());
    const [productModalOpen, setProductModalOpen] = useState(false);

    const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const textColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const textColor1 = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const blueColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;


    const colorDanger = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
    const colorDanger1 = mode === 'dark' ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100;
    const colorDanger3 = mode === 'dark' ? DARKCOLORS.Danger600 : LIGHTCOLORS.Danger600;
    const colorDanger2 = mode === 'dark' ? DARKCOLORS.Danger300 : LIGHTCOLORS.Danger300;

    const colorWarning = mode === 'dark' ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
    const colorWarning1 = mode === 'dark' ? DARKCOLORS.Warning100 : LIGHTCOLORS.Warning100;
    const colorWarning3 = mode === 'dark' ? DARKCOLORS.Warning300 : LIGHTCOLORS.Warning300;
    const colorWarning2 = mode === 'dark' ? DARKCOLORS.Warning700 : LIGHTCOLORS.Warning700;


    const colorSuccess = mode === 'dark' ? DARKCOLORS.Success700 : LIGHTCOLORS.Success700;
    const colorSuccess1 = mode === 'dark' ? DARKCOLORS.Success100 : LIGHTCOLORS.Success100;
    const colorSuccess3 = mode === 'dark' ? DARKCOLORS.Success500 : LIGHTCOLORS.Success500;
    const colorSecondary3 = mode === 'dark' ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
  const greyBadgeText = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;



    const baseBlack = mode === 'dark' ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;
    const inputBoxColor1 = mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;

    // Greyscale badge for missing (null) backend data
    const greyBadgeBg = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const greyBadgeBorder = mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
    // const greyBadgeText = mode === 'dark' ? DARKCOLORS.Success500 : LIGHTCOLORS.Success700;

    const machineTypeStr = machineData?.vendingMachine?.machineType || '';
    const isReturnMachine = machineTypeStr.toLowerCase().includes('return') || machineTypeStr.toLowerCase().includes('recycle') || machineTypeStr.toLowerCase().includes('greeny');
    const translatedMachineType = machineTypeStr ? t(`vendingMachines.${machineTypeStr.split(' ')[0]}`, { defaultValue: machineTypeStr }) : "Unknown";

    // Bowl and bottle capacity data from API response
  const bowlCashback = machineData?.vendingMachine?.bowlBinLevel ?? 0;
const bottleCashback = machineData?.vendingMachine?.bottleBinLevel ?? 0;
const bowlMaxCapacity = machineData?.vendingMachine?.machineConfig?.bowlCapacity ?? 0;
const bottleMaxCapacity = machineData?.vendingMachine?.machineConfig?.bottleCapacity ?? 0;
    // const bowlCount = bowlMaxCapacity ? Math.round(Number(bowlCashback) / 100 * Number(bowlMaxCapacity)) : 0;
    // const bottleCount = bottleMaxCapacity ? Math.round(Number(bottleCashback) / 100 * Number(bottleMaxCapacity)) : 0;

    const bowlCount = Number(machineData?.vendingMachine?.bowlBinLevel ?? 0);
const bottleCount = Number(machineData?.vendingMachine?.bottleBinLevel ?? 0);
  const badgecolor =  DARKCOLORS.BodyDarkBackground;



    const getStatusBadgeStyles = (status) => {
        // Greyscale badge for missing (null) backend data
        if (!status) {
            return {
                backgroundColor: greyBadgeBg,
                border: `1px solid ${greyBadgeBorder}`,
                //dotColor: greyBadgeText,
            };
        }
        switch (status) {
           case "Active":
    return {
        backgroundColor: colorSuccess1,
        border: `1px solid ${colorSuccess}`,
        color: colorSuccess,
    };
            case "Inactive":
                return {
                    backgroundColor: colorDanger1,
                    border: `1px solid ${colorDanger}`,
                    //dotColor: colorDanger,
                    color: colorDanger,
                };
            case "Expired":
                return {
                    backgroundColor: colorDanger1,
                    border: `1px solid ${colorDanger}`,
                    //dotColor: colorDanger,
                };
            default:
                return {
                    backgroundColor: colorWarning1,
                    border: `1px solid ${colorWarning}`,
                   // dotColor: colorWarning,
                };
        }
    };

    const getConditionBadgeStyles = (condition) => {
        if (!condition) {
            return {
                backgroundColor: greyBadgeBg,
                border: `1px solid ${greyBadgeBorder}`,
                color: greyBadgeText,
            };
        }
        const isNormal = condition === 'Normal';
        return {
            backgroundColor: isNormal ? colorSuccess1 : colorDanger1,
            border: `1px solid ${isNormal ? colorSuccess : colorDanger}`,
            color: isNormal ? colorSuccess : colorDanger,
        };
    };

    const getCapacityStatus = (percentage) => {
        if (percentage >= 100) {
            return {
                label: t('dashboard.full'),
                badgeBg: colorDanger1,
                badgeBorder: colorDanger2,
                dotColor: colorDanger3,
                textColor: colorDanger3,
                barColor: colorDanger,
            };
        } else if (percentage >= 75) {
            return {
                label: t('dashboard.almostFull'),
                badgeBg: colorWarning1,
                badgeBorder: colorWarning,
                //dotColor: colorWarning2,
                textColor: colorWarning2,
                barColor: colorWarning,
            };
        } else {
            return {
                label: t('dashboard.normal'),
                badgeBg: colorSuccess1,
                badgeBorder: colorSuccess,
                //dotColor: colorSuccess,
                textColor: colorSuccess,
                barColor: colorSuccess3,
            };
        }
    };

    const isTablet = useMediaQuery('(max-width:910px)');
    const isMobile = useMediaQuery('(max-width:610px)');
    const isSmall = useMediaQuery('(max-width:410px)');

    const handleSettingsChange = (updatedSettings) => {
        // console.log(t('vendingMachines.updatedSettings'), updatedSettings);
        setSettings(updatedSettings);
        setUnsavedChanges(true);
    };

    const handleBackClick = () => {
        if (unsavedChanges) {
            setShowConfirmation(true);
        } else {
            navigate(ROUTES.VENDING_MACHINES, { state: { franchise } });
        }
    };

    const handleDiscardChanges = () => {
        setUnsavedChanges(false);
        setShowConfirmation(false);
        navigate(ROUTES.VENDING_MACHINES, { state: { franchise } });
    };

    const handleSaveChanges = async () => {
        await handleSaveSettings(settings);
        setUnsavedChanges(false);
        setShowConfirmation(false);
        setTimeout(() => {
            navigate(ROUTES.VENDING_MACHINES, { state: { franchise } });
        }, 2500);
    };

    const handleTabChange = async (tab) => {
        setActiveTab(tab);
        if (tab === "aisle" && machineData?.vendingMachine?.machineNumber) {
            await refreshSlots();
        }
    };

    const refreshSlots = async (updatedTags) => {
        try {
            setSelectedTags(updatedTags);
            setLoading(true);
            const data = await getAisleData(machineData.vendingMachine.machineNumber, updatedTags);
            setAisleData(data);
            setModalOpen(false)
        } catch (error) {
            console.error(t("vendingMachines.errorFetchingAisleData"), error);
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        window.scrollTo(0, 0);
        // Replace with your backend API endpoint to fetch machine details
        const fetchMachineDetails = async () => {
            try {
                if (franchise) {
                    const response = await getFranchiseMachineDetails(id);
                    setMachineData(response);
                } else {
                    const response = await getMachineDetails(id);
                    setMachineData(response);
                    setSettings({
                        minTemperature: response?.vendingMachine?.temperatureHumidity?.minTemperature || 0,
                        maxTemperature: response?.vendingMachine?.temperatureHumidity?.maxTemperature || 0,
                        cabinetTemperature: response?.vendingMachine?.temperatureHumidity?.cabinetTemperature || 0,
                        heatingTemperature: response?.vendingMachine?.temperatureHumidity?.heatingTemperature || 0,
                        preHeatTemperature: response?.vendingMachine?.temperatureHumidity?.preHeatTemperature || 0,
                        heatingTime: response?.vendingMachine?.temperatureHumidity?.heatingTime || 0,
                        defrostInterval: response?.vendingMachine?.temperatureHumidity?.defrostInterval || 0,
                        defrostDuration: response?.vendingMachine?.temperatureHumidity?.defrostDuration || 0,
                    });
                }
            } catch (error) {
                console.error(t("vendingMachines.errorFetchingMachineDetails"), error);
                // Fallback to data passed via navigation state (e.g. for demo machines)
                if (location.state?.machineData) {
                    const fb = location.state.machineData;
                    setMachineData({
                        ...fb,
                        vendingMachine: {
                            ...(fb.vendingMachine || {}),
                            status: fb.status || fb.vendingMachine?.status,
                            machineCondition: fb.condition || fb.vendingMachine?.machineCondition,
                            machineNumber: fb.machineNumber || fb.vendingMachine?.machineNumber,
                            location: fb.location || fb.vendingMachine?.location,
                        },
                    });
                }
            } finally {
                setLoading(false);
            }
        };
        fetchMachineDetails();
    }, [id]);

    const handleSaveSettings = async (updatedSettings) => {
        try {
            setLoading(true)
            await updateMachineSettings(id, updatedSettings);
            setSuccessNotification(true);
            setSettings(updatedSettings);
            setUnsavedChanges(false);
        } catch (error) {
            console.error(t("vendingMachines.errorUpdatingSettings"), error);
            alert(t("vendingMachines.failedUpdateSettings"));
        } finally {
            setLoading(false)
        }
    };

    const handleViewMore = () => {
        navigate(ROUTES.ADD_MACHINE, {
            state: {
                mode: "update", // Specify the mode
                machineData, // Pass the existing machine data
            },
        });
    };

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    const handleProductModalOpen = () => {
        setError(""); 
        setProductModalOpen(true);
    };
    const handleProductModalClose = () => setProductModalOpen(false);

    // const handleSlotClick = (slotId) => {
    //     setSelectedSlotIds(prev => {
    //         const updated = new Set(prev);
    //         updated.has(slotId) ? updated.delete(slotId) : updated.add(slotId);
    //         return updated;
    //     });
    // };

    const handleSlotClick = (slotId, forceState = null) => {
        setSelectedSlotIds(prev => {
            const updated = new Set(prev);

            if (forceState === true) {
                updated.add(slotId);
            } else if (forceState === false) {
                updated.delete(slotId);
            } else {
                updated.has(slotId) ? updated.delete(slotId) : updated.add(slotId);
            }

            return updated;
        });
    };

    const handleAddProduct = async (product) => {
        const payload = {
            ...product,
            slotIds: Array.from(selectedSlotIds),
        };
        try {
            setError("");
            await updateBulkSlot(payload);
            handleProductModalClose();
            setSuccessNotification(true)
            // Add delay to show the notification before navigating
            setTimeout(() => {
                if (refreshSlots) {
                    refreshSlots();
                    setSelectedSlotIds(new Set());
                }
            }, 2000); // 3-second delay
            setSelectedSlotIds(new Set());
        } catch (error) {
            console.error(t("vendingMachines.failedUpdateSlot"), error);
            setError(error.detail || t("vendingMachines.errorUpdatingSlotMessage"));
            throw error; // re-throw so ProductModal1 doesn't close on failure
        }
    };

    const handleEmptySlots = async () => {
        const payload = {
            vendingMachineId: machineData.vendingMachine.id,
            slotIds: Array.from(selectedSlotIds),
        };

        // console.log(t('vendingMachines.emptySlotPayload'), payload);
        try {
            setError("");
            await emptySlots(payload);
            // handleProductModalClose();
            setSuccessNotification(true)
            // Add delay to show the notification before navigating
            setTimeout(() => {
                if (refreshSlots) {
                    refreshSlots();
                }
            }, 2000); // 3-second delay
            setSelectedSlotIds(new Set());
        } catch (error) {
            console.error(t("vendingMachines.failedUpdateSlot"), error);
            setError(error.detail || t("vendingMachines.errorUpdatingSlotMessage"));
        }
    };

    return (
        <div className="main-container">
            {loading && <LoadingAnimation />}
            {/* Back Button and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                    sx={{
                        color: blueColor,
                        backgroundColor: backgroundCard,
                        padding: '0.6rem',
                        borderRadius: '50%',
                        marginRight: '1rem',
                        '&:hover': { backgroundColor: backgroundCard },
                    }}
                    onClick={handleBackClick}
                >
                    <WestIcon fontSize="small" />
                </IconButton>
                <h2 className='addmachine-text-heading' title={machineData?.vendingMachine?.location} style={{
                    margin: 0,
                    fontSize: '1.5rem',
                    maxWidth: '400px',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                }}
                    sx={{
                        "@media (max-width: 768px)": {
                            fontSize: "1rem",
                        },
                        "@media (max-width: 500px)": {
                            fontSize: "1.2rem",
                        },
                        "@media (max-width: 320px)": {
                            fontSize: "0.1rem",
                        },
                    }}>{machineData?.vendingMachine?.location}</h2>
            </Box>

            {/* Main Content Section */}
            <Box sx={{ background: backgroundCard, p: 5, margin: "20px 0px", borderRadius: "12px" }}>
                {!franchise && !isReturnMachine && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, marginBottom: "21px", width: "100%" }}>
                        {/* Tabs for toggling between views */}
                        <Box
                            sx={{
                                display: "flex",
                                backgroundColor: inputBoxColor,
                                borderRadius: "8px",
                                padding: "4px",
                                width: "fit-content",
                            }}
                        >
                            <Button
                                onClick={() => handleTabChange("machine")}
                                sx={{
                                    backgroundColor: activeTab === "machine" ? backgroundCard : "transparent",
                                    color: activeTab === "machine" ? blueColor : textColor,
                                    borderRadius: "8px",
                                    width: "170px",
                                    textTransform: "none",
                                    padding: "8px 40px",
                                    "@media (max-width: 470px)": {
                                        width: "110px",
                                        padding: "6px 14px",
                                    }
                                }}
                            >
                                <p className="machine-name-text"> {t('vendingMachines.Machine')}</p>
                            </Button>
                            <Button
                                onClick={() => handleTabChange("aisle")}
                                sx={{
                                    backgroundColor: activeTab === "aisle" ? backgroundCard : "transparent",
                                    color: activeTab === "aisle" ? blueColor : textColor,
                                    borderRadius: "8px",
                                    width: "170px",
                                    textTransform: "none",
                                    padding: "8px 40px",
                                    "@media (max-width: 470px)": {
                                        width: "110px",
                                        padding: "6px 14px",
                                    }
                                }}
                            >
                                <p className="machine-name-text"> {t('vendingMachines.Aisle')}</p>
                            </Button>
                        </Box>

                        {activeTab === "aisle" &&
                            <IconButton className='add-machine' id={isMobile ? "machineStreet1" : ""} onClick={handleModalOpen}
                                sx={{
                                    borderRadius: '40px', padding: '11px 20px', backgroundColor: LIGHTCOLORS.Secondary500, color: "#000", mx: '7px',
                                    "&:hover": {
                                        backgroundColor: LIGHTCOLORS.Secondary500, // Same as default
                                        color: "#000", // Same as default
                                        boxShadow: "none", // Prevent shadow effects
                                    },
                                }} >
                                {isMobile ? "" : t('vendingMachines.dietaryPreferences')}
                                <svg style={{ marginLeft: isMobile ? "" : '10px' }} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M4.16602 8.33366H5.83268C7.49935 8.33366 8.33268 7.50033 8.33268 5.83366V4.16699C8.33268 2.50033 7.49935 1.66699 5.83268 1.66699H4.16602C2.49935 1.66699 1.66602 2.50033 1.66602 4.16699V5.83366C1.66602 7.50033 2.49935 8.33366 4.16602 8.33366Z" stroke="black" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M15.8327 1.66699H14.166C12.4993 1.66699 11.666 2.50033 11.666 4.16699V5.83366C11.666 7.50033 12.4993 8.33366 14.166 8.33366H15.8327C17.4993 8.33366 18.3327 7.50033 18.3327 5.83366V4.16699" stroke="black" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M14.166 18.3337H15.8327C17.4993 18.3337 18.3327 17.5003 18.3327 15.8337V14.167C18.3327 12.5003 17.4993 11.667 15.8327 11.667H14.166C12.4993 11.667 11.666 12.5003 11.666 14.167V15.8337C11.666 17.5003 12.4993 18.3337 14.166 18.3337Z" stroke="black" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M4.16602 18.3337H5.83268C7.49935 18.3337 8.33268 17.5003 8.33268 15.8337V14.167C8.33268 12.5003 7.49935 11.667 5.83268 11.667H4.16602C2.49935 11.667 1.66602 12.5003 1.66602 14.167V15.8337" stroke="black" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </IconButton>
                        }
                    </Box>
                )}
                {/* Conditional Rendering Based on Active Tab */}
                {activeTab === "machine" && (
                    <>
                        <Box
                            sx={{
                                background: backgroundCard,
                                p: 5,
                                margin: "20px 0px",
                                borderRadius: "12px",
                                border: `1px solid ${inputBoxColor}`, // Add border
                                position: "relative", // Make position relative to align button inside
                                "@media (max-width: 768px)": {
                                    px: 2,
                                },
                                "@media (max-width: 370px)": {
                                    px: 1,
                                },
                            }}>
                            {/* Machine Details */}
                            <Grid container spacing={1} sx={{
                                "@media (max-width: 720px)": {
                                    flexDirection: "column-reverse",
                                },
                            }}>
                                {/* Left Section */}
                                <Grid item xs={12} md={8} sx={{
                                    "& > .MuiGrid-container": {
                                        "@media (max-width: 720px)": {
                                            flexDirection: "column",
                                            "& .MuiGrid-item": {
                                                maxWidth: "100%",
                                                flexBasis: "100%",
                                            },
                                        },
                                    },
                                }}>
                                    <Grid container spacing={2}>
                                        <Grid item className="machine-item" xs={6}>
                                            <Box sx={{
                                                p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                "@media (max-width: 450px)": {
                                                    p: 1.5,
                                                },
                                                "& p": {
                                                    margin: 0,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}>
                                                <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.MachineNo")}</p>
                                                <p id="machine-margin1" className="add-machine add-machine2 machine-margin" style={{ margin: "0px", color: textColor }}>{machineData?.vendingMachine?.machineNumber || "Unknown"}</p>
                                            </Box>
                                        </Grid>
                                        <Grid className="machine-item" item xs={6}>
                                            <Box sx={{
                                                p: 1.3, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                "@media (max-width: 450px)": {
                                                    p: 1.5,
                                                },
                                                "& p": {
                                                    margin: 0,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}
                                            >
                                                <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.status")}</p>
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        padding: "6px 10px",
                                                        borderRadius: "99px",
                                                        backgroundColor: getStatusBadgeStyles(machineData?.vendingMachine?.status).backgroundColor,
                                                        border: getStatusBadgeStyles(machineData?.vendingMachine?.status).border,
                                                    }}
                                                >
                                                   
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            color: getStatusBadgeStyles(machineData?.vendingMachine?.status).color,
                                                            fontFamily: "Satoshi-medium",
                                                        }}
                                                    >
                                                        {machineData?.vendingMachine?.status || t('sales_analytics.NoData')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid className="machine-item" item xs={6}>
                                            <Box sx={{
                                                p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                "@media (max-width: 450px)": {
                                                    p: 1.5,
                                                },
                                                "& p": {
                                                    margin: 0,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}>
                                                <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.MachineType")}</p>
                                                <p className="add-machine add-machine3" style={{ margin: "0px", color: textColor }}>{translatedMachineType}</p>
                                            </Box>
                                        </Grid>
                                        <Grid className="machine-item" item xs={6}>
                                            <Box sx={{
                                                p: 1.3, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                "@media (max-width: 450px)": {
                                                    p: 1.5,
                                                },
                                                "& p": {
                                                    margin: 0,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}>
                                                <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.condition")}</p>
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        padding: "6px 10px",
                                                        borderRadius: "99px",
                                                        backgroundColor: getConditionBadgeStyles(machineData?.vendingMachine?.machineCondition).backgroundColor,
                                                        border: getConditionBadgeStyles(machineData?.vendingMachine?.machineCondition).border,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            color: getConditionBadgeStyles(machineData?.vendingMachine?.machineCondition).color,
                                                            fontFamily: "Satoshi-medium",
                                                        }}
                                                    >
                                                        {!machineData?.vendingMachine?.machineCondition ? t('sales_analytics.NoData') : machineData?.vendingMachine?.machineCondition === 'Normal' ? 'Normal ' : 'Abnormal '}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid className="machine-item" item xs={6}>
                                            <Box sx={{
                                                p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                "@media (max-width: 450px)": {
                                                    p: 1.5,
                                                },
                                                "& p": {
                                                    margin: 0,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}>
                                                <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.Location")}</p>
                                                <p id="machine-margin6" className="add-machine " style={{
                                                    margin: "0px", color: textColor, maxWidth: '150px',
                                                    whiteSpace: 'nowrap',
                                                    // overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>{machineData?.vendingMachine?.location || "Unknown"}</p>
                                            </Box>
                                        </Grid>
                                        {!franchise && !isReturnMachine && (
                                            <Grid className="machine-item" item xs={6}>
                                                <Box sx={{
                                                    p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                    "@media (max-width: 450px)": {
                                                        p: 1.5,
                                                    },
                                                    "& p": {
                                                        margin: 0,
                                                        whiteSpace: "nowrap",
                                                    },
                                                }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("dashboard.inventoryLevel")}</p>
                                                        {machineData?.inventoryLevel < 70 && (
                                                            <Chip
                                                                label={machineData?.inventoryLevel < 30 ? t('dashboard.low') : t('dashboard.restock')}
                                                                sx={{
                                                                    backgroundColor: machineData?.inventoryLevel < 30 ? "#FF4141" : "#EBBC15",
                                                                    color: badgecolor,
                                                                    fontSize: "10px",
                                                                    marginRight: "8px",
                                                                    padding: "10px 5px",
                                                                    height: "1px",
                                                                    "@media (max-width: 385px)": {
                                                                        marginRight: "0px",
                                                                        maxWidth: '40%',
                                                                        height: '20px',
                                                                        fontSize: "8px",
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography sx={{ display: "flex", alignItems: "center" }}>
                                                        <p className="add-machine add-machine1" style={{ margin: "0px", color: textColor }}>{machineData?.inventoryLevel
                                                            ? <>{machineData?.inventoryLevel.toFixed(2)}% <span style={{ fontSize: "10px" }}> ({machineData?.totalQuantity}/{machineData?.totalCapacity})</span></>
                                                            : "-"}</p>
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        {/* {!franchise && isReturnMachine && (
                                            <Grid item xs={6}>
                                                <Box sx={{
                                                    p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                    "@media (max-width: 450px)": {
                                                        p: 1.5,
                                                    },
                                                    "& p": {
                                                        margin: 0,
                                                        whiteSpace: "nowrap",
                                                    },
                                                }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t('vendingMachines.binLevel') || 'Bin Level'}</p>
                                                    </Box>
                                                    <Typography sx={{ display: "flex", alignItems: "center" }}>
                                                        <p className="add-machine add-machine1" style={{ margin: "0px", color: textColor }}>{machineData?.inventoryLevel
                                                            ? <>{machineData?.inventoryLevel.toFixed(2)}% <span style={{ fontSize: "10px" }}> ({machineData?.totalQuantity}/{machineData?.totalCapacity})</span></>
                                                            : "N/A"}</p>
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )} */}
                                        <Grid className="machine-item" item xs={6}>
                                            <Box sx={{
                                                p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                "@media (max-width: 450px)": {
                                                    p: 1.5,
                                                },
                                                "& p": {
                                                    margin: 0,
                                                    whiteSpace: "nowrap",
                                                },
                                            }}>
                                                <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.StreetName")}</p>
                                                <p className="add-machine add-machine2" style={{ margin: "0px", color: textColor }}>{machineData?.vendingMachine?.street ||"Unknown"}</p>
                                            </Box>
                                        </Grid>
                                        {!isReturnMachine && (
                                            <Grid className="machine-item" item xs={6}>
                                                <Box sx={{
                                                    p: 2, backgroundColor: inputBoxColor, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
                                                    "@media (max-width: 450px)": {
                                                        p: 1.5,
                                                    },
                                                    "& p": {
                                                        margin: 0,
                                                        whiteSpace: "nowrap",
                                                    },
                                                }}>
                                                    <p className="label-text-sec label-text-sec1" style={{ margin: "0px" }}>{t("vendingMachines.liveTemp")}</p>
                                                    <p id="machine-margin6" className="add-machine " style={{
                                                        margin: "0px", color: textColor, maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        // overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'flex', alignItems: 'center', gap: '5px'
                                                    }}>
                                                        {
                                                            machineData?.vendingMachine?.temperatureHumidity?.liveTemperature !== null
                                                                ? (
                                                                    machineData?.vendingMachine?.temperatureHumidity?.liveTemperature >= 0
                                                                        ? <>
                                                                             {machineData?.vendingMachine?.temperatureHumidity?.liveTemperature}°C
                                                                        </>
                                                                        : <>
                                                                            <Typography><svg width="12" height="12" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M4.73684 7.45158L2.93632 9.22526C2.88719 9.27474 2.82719 9.29947 2.75632 9.29947C2.68544 9.29947 2.62579 9.27053 2.57737 9.21263C2.51912 9.16351 2.49 9.10351 2.49 9.03263C2.49 8.96175 2.51895 8.89754 2.57684 8.84L4.73684 6.68V5.26316H3.32L1.14632 7.43632C1.09719 7.48544 1.03754 7.51 0.967368 7.51C0.897193 7.51 0.832807 7.48105 0.774211 7.42316C0.725088 7.37403 0.700526 7.31403 0.700526 7.24316C0.700526 7.17228 0.725088 7.11263 0.774211 7.06421L2.54842 5.26316H0.25C0.18193 5.26316 0.123158 5.23807 0.0736843 5.18789C0.0245615 5.13772 0 5.07509 0 5C0 4.93193 0.0250877 4.87105 0.0752631 4.81737C0.125789 4.76368 0.188421 4.73684 0.263158 4.73684H2.54842L0.774737 2.94947C0.725263 2.90035 0.700526 2.84053 0.700526 2.77C0.700526 2.69947 0.729474 2.63509 0.787368 2.57684C0.836491 2.52772 0.896491 2.50316 0.967368 2.50316C1.03825 2.50316 1.10246 2.52772 1.16 2.57684L3.32 4.73684H4.73684V3.32L2.56368 1.16C2.51456 1.11088 2.49 1.05105 2.49 0.980526C2.49 0.91 2.51895 0.845614 2.57684 0.787368C2.62596 0.738246 2.68597 0.713684 2.75684 0.713684C2.82772 0.713684 2.88737 0.738246 2.93579 0.787368L4.73684 2.54842V0.263158C4.73684 0.195088 4.76193 0.134211 4.81211 0.0805264C4.86228 0.0268422 4.92491 0 5 0C5.06807 0 5.12895 0.0268422 5.18263 0.0805264C5.23632 0.134211 5.26316 0.195088 5.26316 0.263158V2.54842L7.05053 0.787368C7.09965 0.738246 7.15947 0.713684 7.23 0.713684C7.30053 0.713684 7.36491 0.738246 7.42316 0.787368C7.47228 0.845263 7.49684 0.909474 7.49684 0.98C7.49684 1.05053 7.47228 1.11053 7.42316 1.16L5.26316 3.32V4.73684H6.68L8.84 2.57684C8.88912 2.52772 8.94912 2.50316 9.02 2.50316C9.09088 2.50316 9.15509 2.53211 9.21263 2.59C9.26175 2.63912 9.28632 2.69895 9.28632 2.76947C9.28632 2.84 9.26175 2.9 9.21263 2.94947L7.45158 4.73684H9.73684C9.80491 4.73684 9.86579 4.76368 9.91947 4.81737C9.97316 4.87105 10 4.93193 10 5C10 5.07509 9.97316 5.13772 9.91947 5.18789C9.86579 5.23807 9.80491 5.26316 9.73684 5.26316H7.45158L9.21263 7.06368C9.26175 7.11281 9.28632 7.17281 9.28632 7.24368C9.28632 7.31456 9.26175 7.37421 9.21263 7.42263C9.15439 7.48088 9.09 7.51 9.01947 7.51C8.94895 7.51 8.8893 7.48105 8.84053 7.42316L6.67947 5.26316H5.26316V6.68L7.42316 8.85368C7.47228 8.90281 7.49684 8.96246 7.49684 9.03263C7.49684 9.10281 7.4679 9.16719 7.41 9.22579C7.36088 9.27491 7.30088 9.29947 7.23 9.29947C7.15912 9.29947 7.09947 9.27491 7.05105 9.22579L5.26316 7.45158V9.75C5.26316 9.81807 5.23632 9.87684 5.18263 9.92632C5.12895 9.97544 5.06807 10 5 10C4.92526 10 4.86263 9.97491 4.81211 9.92474C4.76158 9.87456 4.73649 9.81193 4.73684 9.73684V7.45158Z" fill="#4C33DB" />
                                                                            </svg></Typography>
                                                                            {machineData?.vendingMachine?.temperatureHumidity?.liveTemperature}°C
                                                                        </>
                                                                )
                                                                : "-"
                                                        }</p>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>

                                {/* Right Section (Image) */}
                                <Grid className="image-item" item xs={12} md={4}>
                                    <Box
                                        sx={{
                                            width: "100%",           // Ensures the container takes the full width of its grid column
                                            aspectRatio: "4 / 3",    // Enforces a consistent 4:3 aspect ratio
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            overflow: "hidden",
                                            borderRadius: "12px",
                                            margin: {
                                                sm: "20px 0 ",
                                                md: " 0 ",

                                            },
                                        }}
                                    >
                                        {machineData?.imageUrl ? (
                                            <img
                                                src={machineData.imageUrl}
                                                alt={machineData?.name || "No Image"}
                                                style={{
                                                    width: "100%",          // Fills the 4:3 container width
                                                    height: "100%",         // Fills the 4:3 container height
                                                    objectFit: "cover",     // Crops excess to preserve the container's 4:3 ratio
                                                    borderRadius: "12px",   // Adds rounded corners
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ textAlign: "center", color: mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40 }}>
                                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z" stroke="currentColor" strokeWidth="2" fill="none" />
                                                    <path d="M4 8H20" stroke="currentColor" strokeWidth="2" />
                                                    <circle cx="9" cy="13" r="2" fill="currentColor" />
                                                    <path d="M4 16L8 12L12 16L16 11L20 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <Typography sx={{ fontSize: "12px", mt: 1, fontFamily: "Satoshi", color: mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40 }}>
                                                    {t('vendingMachines.noImage') || 'No Image'}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* View More Button */}
                            {user?.roles?.includes('ROLE_SUPER_ADMIN') && (
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        marginTop: "20px",
                                        position: "absolute",
                                        bottom: "-15px",
                                        left: "50%",
                                        transform: "translateX(-50%)", // Center horizontally
                                    }}
                                >
                                    <button type="button" className="view-more-btn" style={{ background: backgroundCard, color: blueColor, borderColor: blueColor }} onClick={() => handleViewMore()}>
                                        {t("vendingMachines.viewMore")}
                                    </button>
                                </Box>
                            )}
                        </Box>

                        {/* Temperature and Defrost Settings */}
                        {!franchise && !isReturnMachine && (
                            <TemperatureAndDefrostSettings
                                initialSettings={settings}
                                onSave={handleSaveSettings}
                                onChange={handleSettingsChange}
                                machineType={machineData?.vendingMachine?.machineType || ''}
                            />
                        )}
                        {!isReturnMachine && (
                            <TempReadings machineId={id || ''} machineType={machineData?.vendingMachine?.machineType || ''} />
                        )}

                        {/* Bowl and Bottle Capacity for Return/Recycle Machines */}
                         {!franchise && isReturnMachine && (
                            <Grid container spacing={2} sx={{ mt: 13 }}>
                               
                                <Grid item xs={12} md={6}>
                                    <Box sx={{
                                        p: 1.5,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: '30px',
                                       
                                        backgroundColor: inputBoxColor,
                                        borderRadius: "12px",
                                        border: `1px solid ${inputBoxColor}`
                                    }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                            <Typography className="return-titles" sx={{ color: textColor1 }}>
                                                {t('vendingMachines.bowlCapacity') || 'Bowl Capacity'}
                                            </Typography>

                                            {(() => {
                                                const bowlStatus = getCapacityStatus(bowlCashback);
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            padding: "6px 10px",
                                                            borderRadius: "99px",
                                                            backgroundColor: bowlStatus.badgeBg,
                                                            border: `1px solid ${bowlStatus.badgeBorder}`,
                                                        }}
                                                    >
                                                     
                                                        <Typography
                                                            sx={{
                                                                fontSize: "10px",
                                                                fontWeight: 500,
                                                                color: bowlStatus.textColor,
                                                                fontFamily: "Satoshi-medium",
                                                            }}
                                                        >
                                                            {bowlStatus.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>

                                        <div>
                                            <Typography className="return-percentage" sx={{ color: textColor}}>
                                                {bowlCashback}%
                                            </Typography>

                                            <Typography sx={{
                                                fontFamily: "Satoshi",
                                                fontSize: "15px",
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: "120%",
                                                letterSpacing: "0.3px",
                                                color: textColor1,
                                               
                                            }}>
                                                {bowlCount} {" "}
                                                <Typography component="span" sx={{ fontSize: "13px", color: textColor,gap: "4px" }}>{t('vendingMachines.bowls') || 'Bowls'}</Typography>
                                            </Typography>
                                            <Box sx={{ display: "flex", gap: "4px", borderRadius: "12px", mb: 1.5, p: '5px', backgroundColor: backgroundCard, }}>
                                                {[1, 2, 3, 4].map((seg) => (
                                                    <Box
                                                        key={seg}
                                                        sx={{
                                                            flex: 1,
                                                            height: "8px",
                                                            borderRadius: "4px",
                                                            backgroundColor: (() => { const pct = bowlCashback; const filled = Math.min(4, Math.ceil(pct / 25)); return seg <= filled ? getCapacityStatus(pct).barColor : inputBoxColor1; })(),
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </div>



                                    </Box>
                                </Grid>
                               
                                <Grid item xs={12} md={6}>
                                    <Box sx={{
                                        p: 1.5,
                                        backgroundColor: inputBoxColor,
                                        borderRadius: "12px",
                                         display: "flex",
                                        flexDirection: "column",
                                        gap: '30px',
                                        border: `1px solid ${inputBoxColor}`
                                    }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                            <Typography className="return-titles" sx={{  color: textColor1 }}>
                                                {t('vendingMachines.bottleCapacity') || 'Bottle Capacity'}
                                            </Typography>
                                            {(() => {
                                                const bottlePct = bottleCashback;
                                                const bottleStatus = getCapacityStatus(bottlePct);
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            padding: "6px 10px",
                                                            borderRadius: "99px",
                                                            backgroundColor: bottleStatus.badgeBg,
                                                            border: `1px solid ${bottleStatus.badgeBorder}`,
                                                        }}
                                                    >
                                                       
                                                        <Typography
                                                            sx={{
                                                                fontSize: "10px",
                                                                fontWeight: 500,
                                                                color: bottleStatus.textColor,
                                                                fontFamily: "Satoshi-medium",
                                                            }}
                                                        >
                                                            {bottleStatus.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                        
                                        <div>
                                        
                                        
                                        
                                             <Typography  className="return-percentage"  sx={{  color: textColor,  }}>
                                                {bottleCashback}%
                                            </Typography>
                                            <Typography sx={{fontSize: "15px",
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: "120%",
                                                letterSpacing: "0.3px",
                                                color: textColor1 }}>
                                                {bottleCount} {" "}
                                                 <Typography component="span" sx={{ fontSize: "13px", color: textColor }}>{t('vendingMachines.bottles') || 'Bottles'}</Typography>
                                            </Typography>
                                              <Box sx={{ display: "flex", gap: "4px", borderRadius: "12px", mb: 1.5, p: '5px', backgroundColor: backgroundCard, }}>
                                        
                                                {[1, 2, 3, 4].map((seg) => (
                                                    <Box
                                                        key={seg}
                                                        sx={{
                                                            flex: 1,
                                                            height: "8px",
                                                            borderRadius: "4px",
                                                            backgroundColor: (() => { const pct = bottleCashback; const filled = Math.min(4, Math.ceil(pct / 25)); return seg <= filled ? getCapacityStatus(pct).barColor : inputBoxColor1; })(),
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                           
                                        </div>

                                    </Box>
                                </Grid>
                            </Grid>
                        )} 

                    </>
                )}

                {/* Aisle Details View */}
                {activeTab === "aisle" && (
                    <Box>
                        <Stack
                            className="aisle_stack_sec"
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                                position: "fixed",
                                bottom: 24,
                                zIndex: 1000,
                                transform: selectedSlotIds.size > 0
                                    ? "translateX(-50%) translateY(0) scale(1)"
                                    : "translateX(-50%) translateY(100%) scale(0)",
                                opacity: selectedSlotIds.size > 0 ? 1 : 0,
                                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                backgroundColor: mode === 'dark' ? 'rgba(30, 30, 30, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: "blur(12px)",
                                borderRadius: "16px",
                                left: "55%",
                                padding: "12px 24px",
                                boxShadow: mode === 'dark'
                                    ? "0 4px 30px rgba(0, 0, 0, 0.5)"
                                    : "0 4px 30px rgba(0, 0, 0, 0.1)",
                                border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                pointerEvents: selectedSlotIds.size > 0 ? 'auto' : 'none',
                                "@media (max-width: 426px)": {
                                    // top: 24,
                                    bottom: "auto",
                                    transform: selectedSlotIds.size > 0
                                        ? "translateX(-50%) translateY(0) scale(1)"
                                        : "translateX(-50%) translateY(-100%) scale(0)",

                                },
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={handleProductModalOpen}
                                disabled={selectedSlotIds.size === 0}
                                sx={{
                                    backgroundColor: colorSecondary3,
                                    textTransform: "none",
                                    borderRadius: "24px",
                                    px: 3,
                                    py: 1.5,
                                    color: baseBlack
                                }}
                                className="add-machine "
                                id="addProduct_asils_buttons"
                            >
                                {t('vendingMachines.addProduct')}
                            </Button>

                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleEmptySlots}
                                disabled={selectedSlotIds.size === 0}
                                sx={{
                                    borderWidth: 2,
                                    borderColor: inputBoxColor1,
                                    textTransform: "none",
                                    borderRadius: "24px",
                                    px: 3,
                                    py: 1.5,
                                    color: textColor1
                                }}
                                id="emptySlots_asils_buttons"

                                className="add-machine "
                            >
                                {t('vendingMachines.emptySlots')}
                            </Button>
                        </Stack>

                        {/* {console.log('aisleData:', aisleData)} */}
                        {aisleData.map((aisle) => (
                            <Grid container spacing={3} key={aisle.slotName} sx={{ marginBottom: "40px" }}>
                                {/* Row Header */}
                                <Grid item
                                    sx={{
                                        flexBasis: "5%", // Takes 5% of the width
                                        maxWidth: isSmall ? "30%" : isTablet ? "10%" : "5%",  // Limits width to 5%
                                        display: "flex",
                                        marginRight: "10px",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}>
                                    <h3 className="slot-key-text">{aisle.slotName}</h3>
                                </Grid>
                                {/* Slots in Row */}
                                <Grid
                                    container
                                    item
                                    sx={{ flexBasis: !isTablet ? "94%" : "99%", maxWidth: "99%" }} // Takes 95% of the width
                                    spacing={3}>
                                    {aisle.slotItems.map((slot) => (
                                        <Grid item xs={12} sm={6} md={3} key={slot.slotId}>
                                            <SlotCard slot={slot} refreshSlots={refreshSlots} tags={tags}
                                                isSelected={selectedSlotIds.has(slot.slotId)}
                                                onSelect={handleSlotClick} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Modal */}
            <DietaryPreferencesModal
                open={modalOpen}
                onClose={handleModalClose}
                onSave={refreshSlots}
                selectedTags={selectedTags}
                tags={tags}
            />

            {/* Use Notification Component */}
            <SuccessNotification
                open={successNotification}
                message={t('membershipCards.successMessage')}
                severity="success"
                onClose={() => setSuccessNotification(false)}
            />

            <ConfirmationModal
                open={showConfirmation}
                onClose={() => setShowConfirmation(false)}
                onSave={handleSaveChanges}
                onDiscard={handleDiscardChanges}
            />

            {/* Modal */}
            <ProductModal1
                open={productModalOpen}
                onClose={handleProductModalClose}
                error={error}
                onSave={handleAddProduct}
                tags={tags}
                refreshSlots={refreshSlots}
            />
        </div>
    );
};

export default MachineDetails;
